import React, { useCallback, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Tooltip,
  Snackbar,
  Switch,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import {
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  InfoOutlined as InfoIcon,
  Instagram,
  YouTube,
  OpenInNew,
  Facebook,
} from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiService, {
  Podcast,
  SocialAccount as SocialAccountType,
} from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import TikTokIcon from "./icons/TikTokIcon";
import SpotifyIcon from "./icons/SpotifyIcon";

const iconColor = {
  instagram: "#E1306C",
  facebook: "#1877F2",
  youtube: "#FF0000",
  tiktok: "#000000",
  spotify: "#1DB954",
};

const PlatformIcon = ({
  size = "small",
  platform,
}: {
  size?: "small" | "inherit" | "large" | "medium";
  platform: "meta" | "youtube" | "tiktok" | "spotify";
}) => {
  switch (platform) {
    case "youtube":
      return <YouTube fontSize={size} sx={{ color: iconColor.youtube }} />;
    case "meta":
      return (
        <Box>
          <Instagram fontSize={size} sx={{ color: iconColor.instagram }} />
          <Facebook fontSize={size} sx={{ color: iconColor.facebook }} />
        </Box>
      );
    case "tiktok":
      return <TikTokIcon fontSize={size} color={iconColor.tiktok} />;
    case "spotify":
      return <SpotifyIcon fontSize={size} />;
  }
};

const platformDisplayName = (p: "meta" | "youtube" | "tiktok" | "spotify") =>
  p === "meta"
    ? "Instagram | Facebook"
    : p === "youtube"
      ? "YouTube"
      : p === "tiktok"
        ? "TikTok"
        : "Spotify";
const platformIcon = (p: "meta" | "youtube" | "tiktok" | "spotify") =>
  PlatformIcon({ platform: p });

const CONNECTED_ACCOUNTS_TOOLTIP_PROPS = {
  placement: "top" as const,
  arrow: true,
  slotProps: {
    tooltip: {
      sx: { maxWidth: 280 },
    },
  },
};

const CONNECTED_ACCOUNTS_ICON_BUTTON_SX = {
  color: (theme: Theme) =>
    theme.palette.mode === "dark"
      ? theme.palette.common.white
      : theme.palette.text.secondary,
  "&:hover": {
    bgcolor: "transparent",
    color: (theme: Theme) =>
      theme.palette.mode === "dark"
        ? theme.palette.common.white
        : theme.palette.text.primary,
  },
};

const CONNECTED_ACCOUNTS_ICON_BUTTON_ERROR_SX = {
  color: "error.main",
  "&:hover": {
    bgcolor: "transparent",
    color: "error.main",
  },
};

const META_MULTI_CHANNEL_TOOLTIP =
  "Si está activado, quien abra este enlace podrá conectar varios podcasts de la empresa en una sola sesión de Facebook. Si está desactivado, solo se vincula este podcast.";

function PodcastInvitations({
  podcast,
  companyId,
  companySlug,
}: {
  podcast: Podcast;
  companyId?: string;
  companySlug: string;
}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [helpOpen, setHelpOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{
    type: "invitation" | "account";
    id: string;
    platform?: string;
  } | null>(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "warning" | "info",
  });

  const { data: invitations, isLoading: invitationsLoading } = useQuery({
    queryKey: ["podcast-invitations", podcast._id],
    queryFn: () =>
      apiService.listPodcastInvitations(companyId || "", podcast._id),
    enabled: !!companyId,
    placeholderData: (previousData) => previousData,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
  const updateMetaMultiPodcast = useMutation({
    mutationFn: ({
      invitationId,
      enabled,
    }: {
      invitationId: string;
      enabled: boolean;
    }) =>
      apiService.updatePodcastInvitationMetaMultiPodcast(
        companyId || "",
        podcast._id,
        invitationId,
        enabled,
      ),
    onSuccess: (_data, { invitationId, enabled }) => {
      queryClient.setQueryData(
        ["podcast-invitations", podcast._id],
        (prev: { invitations?: Array<{ _id: string; metaInviteMultiPodcastEnabled?: boolean }> } | undefined) => {
          if (!prev?.invitations) return prev;
          return {
            ...prev,
            invitations: prev.invitations.map((inv) =>
              inv._id === invitationId
                ? { ...inv, metaInviteMultiPodcastEnabled: enabled }
                : inv,
            ),
          };
        },
      );
      setSnackbar({
        open: true,
        message: enabled
          ? "Este enlace permite conectar varios podcasts"
          : "Este enlace solo conecta este podcast",
        severity: "success",
      });
    },
    onError: (e: any) => {
      setSnackbar({
        open: true,
        message:
          e?.response?.data?.message ||
          "No se pudo actualizar la opción multi-podcast",
        severity: "error",
      });
    },
  });
  const socialAccounts = useQuery<SocialAccountType[]>({
    queryKey: ["podcast-social-accounts", podcast._id],
    queryFn: () => apiService.getSocialAccountsByPodcast(podcast._id),
    enabled: !!podcast._id,
    placeholderData: (previousData) => previousData,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
  const create = useMutation({
    mutationFn: (vars: {
      days: number;
      platform: "meta" | "youtube" | "tiktok" | "spotify";
    }) =>
      apiService.createPodcastInvitation(
        companyId || "",
        podcast._id,
        vars.days,
        vars.platform,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["podcast-invitations", podcast._id],
        exact: true,
      });
      queryClient.invalidateQueries({
        queryKey: ["podcast-social-accounts", podcast._id],
        exact: true,
      });
    },
  });
  const del = useMutation({
    mutationFn: (invitationId: string) =>
      apiService.deletePodcastInvitation(
        companyId || "",
        podcast._id,
        invitationId,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["podcast-invitations", podcast._id],
      });
      queryClient.invalidateQueries({
        queryKey: ["podcast-social-accounts", podcast._id],
      });
    },
  });
  const unlink = useMutation({
    mutationFn: (socialAccountId: string) =>
      apiService.unlinkSocialAccount(socialAccountId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["podcast-social-accounts", podcast._id],
      });
      queryClient.invalidateQueries({
        queryKey: ["podcast-invitations", podcast._id],
      });
    },
  });

  // Confirmation dialog handlers
  const handleDeleteClick = useCallback(
    (type: "invitation" | "account", id: string, platform?: string) => {
      setItemToDelete({ type, id, platform });
      setDeleteConfirmOpen(true);
    },
    [],
  );

  const handleConfirmDelete = useCallback(() => {
    if (itemToDelete) {
      if (itemToDelete.type === "invitation") {
        del.mutate(itemToDelete.id);
      } else {
        unlink.mutate(itemToDelete.id);
      }
    }
    setDeleteConfirmOpen(false);
    setItemToDelete(null);
  }, [del, itemToDelete, unlink]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmOpen(false);
    setItemToDelete(null);
  }, []);

  return (
    <Stack spacing={4}>
      <Stack spacing={2}>
        <Box className="flex items-center justify-between">
          <Box className="flex items-center gap-2">
            <Typography variant="h2">Cuentas conectadas</Typography>
          </Box>
          <Tooltip title="Instrucciones" {...CONNECTED_ACCOUNTS_TOOLTIP_PROPS}>
            <IconButton
              size="small"
              onClick={() => setHelpOpen(true)}
              sx={CONNECTED_ACCOUNTS_ICON_BUTTON_SX}
            >
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Box>
          <Card>
            <CardContent>
              <Stack spacing={2}>
                {(user?.email === "test@podscope.co"
                  ? (["meta"] as const)
                  : (["meta", "youtube", "tiktok", "spotify"] as const)
                ).map((p, index) => {
                  const invsForPlatform =
                    invitations?.invitations?.filter((i) => i.platform === p) ||
                    [];
                  const active = invsForPlatform.find(
                    (i) => i.status === "active",
                  );
                  const used = invsForPlatform
                    .filter((i) => i.status === "used")
                    .sort(
                      (a, b) =>
                        new Date(b.createdAt).getTime() -
                        new Date(a.createdAt).getTime(),
                    )[0];
                  const current = active || used;
                  const accForPlatform = (
                    plat: "meta" | "youtube" | "tiktok" | "spotify",
                  ) => {
                    const map: Record<
                      "meta" | "youtube" | "tiktok" | "spotify",
                      "meta" | "youtube" | "tiktok" | "spotify"
                    > = {
                      meta: "meta",
                      youtube: "youtube",
                      tiktok: "tiktok",
                      spotify: "spotify",
                    };
                    return (socialAccounts.data || []).find(
                      (a) => a.platform === map[plat],
                    );
                  };
                  const linkedAccount = accForPlatform(p);
                  const profileUrl = (() => {
                    if (!linkedAccount) return null;
                    if (p === "youtube")
                      return `https://www.youtube.com/channel/${linkedAccount.accountId}`;
                    if (p === "meta")
                      return linkedAccount.username
                        ? `https://www.instagram.com/${linkedAccount.username}/`
                        : null;
                    // For TikTok avoid linking unless we have a proper handle via profile scope
                    if (p === "tiktok") return linkedAccount?.profileUrl;
                    if (p === "spotify")
                      return `https://open.spotify.com/show/${linkedAccount.accountId}`;
                    return null;
                  })();
                  const invitationUrl = current?.token
                    ? `${window.location.protocol}//${window.location.host}/${companySlug}/invitacion/${current.token}`
                    : null;
                  return (
                    <>
                      <Box key={p} className="py-2">
                        {invitationsLoading ? (
                          <Box className="flex items-center justify-between gap-3 w-full">
                            <Box className="flex items-center gap-2">
                              {platformIcon(p)}
                              <Typography variant="h5">
                                {platformDisplayName(p)}
                              </Typography>
                            </Box>
                            <Box
                              className="flex items-center gap-2"
                              sx={{ height: "30px" }}
                            >
                              <CircularProgress size={18} />
                              <Typography
                                variant="body2"
                                className="text-gray-500"
                              >
                                Cargando…
                              </Typography>
                            </Box>
                          </Box>
                        ) : current && current.status === "active" ? (
                          // Two-row layout for active invitation link
                          <Stack spacing={1}>
                            <Box className="flex items-center gap-2">
                              {platformIcon(p)}
                              <Typography variant="h5">
                                {platformDisplayName(p)}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                                width: "100%",
                              }}
                            >
                              <TextField
                                value={invitationUrl ?? ""}
                                InputProps={{ readOnly: true }}
                                size="small"
                                sx={{
                                  "& .MuiInputBase-root": {
                                    width: "fit-content",
                                  },
                                  "& .MuiInputBase-input": {
                                    direction: "rtl",
                                    fontFamily: "monospace",
                                    padding: 0.6,
                                    width: `calc(${(invitationUrl ?? "").length}ch + 16px)`,
                                    textAlign: "center",
                                  },
                                }}
                              />
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  flexShrink: 0,
                                }}
                              >
                                {p === "meta" && (
                                  <>
                                    <Tooltip
                                      title={META_MULTI_CHANNEL_TOOLTIP}
                                      {...CONNECTED_ACCOUNTS_TOOLTIP_PROPS}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 0.75,
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{ whiteSpace: "nowrap" }}
                                        >
                                          Múltiples canales:
                                        </Typography>
                                        <Switch
                                          size="small"
                                          checked={
                                            !!current.metaInviteMultiPodcastEnabled
                                          }
                                          onChange={(e) =>
                                            updateMetaMultiPodcast.mutate({
                                              invitationId: current._id,
                                              enabled: e.target.checked,
                                            })
                                          }
                                          disabled={
                                            !companyId ||
                                            updateMetaMultiPodcast.isPending
                                          }
                                          sx={{ m: 0 }}
                                        />
                                      </Box>
                                    </Tooltip>
                                    <Divider
                                      orientation="vertical"
                                      flexItem
                                      sx={{ alignSelf: "center", height: 24 }}
                                    />
                                  </>
                                )}
                                <Tooltip
                                  title="Abrir enlace en nueva pestaña"
                                  {...CONNECTED_ACCOUNTS_TOOLTIP_PROPS}
                                >
                                  <IconButton
                                    size="small"
                                    component="a"
                                    href={invitationUrl ?? undefined}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={CONNECTED_ACCOUNTS_ICON_BUTTON_SX}
                                  >
                                    <OpenInNew fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip
                                  title="Copiar enlace"
                                  {...CONNECTED_ACCOUNTS_TOOLTIP_PROPS}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      navigator.clipboard.writeText(
                                        invitationUrl ?? "",
                                      )
                                    }
                                    sx={CONNECTED_ACCOUNTS_ICON_BUTTON_SX}
                                  >
                                    <CopyIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip
                                  title="Eliminar invitación"
                                  {...CONNECTED_ACCOUNTS_TOOLTIP_PROPS}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleDeleteClick(
                                        "invitation",
                                        current._id,
                                      )
                                    }
                                    sx={CONNECTED_ACCOUNTS_ICON_BUTTON_ERROR_SX}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          </Stack>
                        ) : linkedAccount ? (
                          // Two-row layout for connected account
                          <Stack spacing={1}>
                            <Box className="flex items-center gap-2">
                              {platformIcon(p)}
                              <Typography variant="h5">
                                {platformDisplayName(p)}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 2,
                                flexWrap: { xs: "wrap", sm: "nowrap" },
                              }}
                            >
                              <Box className="flex items-center gap-2">
                                <Typography variant="body2" color="primary">
                                  {`Conectado: ${linkedAccount.username || linkedAccount.accountId}`}
                                </Typography>
                                {profileUrl && (
                                  <Tooltip
                                    title="Abrir perfil en la plataforma"
                                    {...CONNECTED_ACCOUNTS_TOOLTIP_PROPS}
                                  >
                                    <IconButton
                                      size="small"
                                      component="a"
                                      href={profileUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={CONNECTED_ACCOUNTS_ICON_BUTTON_SX}
                                    >
                                      <OpenInNew fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Tooltip
                                  title="Abrir enlace en nueva pestaña"
                                  {...CONNECTED_ACCOUNTS_TOOLTIP_PROPS}
                                >
                                  <IconButton
                                    size="small"
                                    component="a"
                                    href={invitationUrl ?? undefined}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={CONNECTED_ACCOUNTS_ICON_BUTTON_SX}
                                  >
                                    <OpenInNew fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip
                                  title="Copiar enlace de invitación para desconectar"
                                  {...CONNECTED_ACCOUNTS_TOOLTIP_PROPS}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      navigator.clipboard.writeText(
                                        invitationUrl ?? "",
                                      )
                                    }
                                    sx={CONNECTED_ACCOUNTS_ICON_BUTTON_SX}
                                  >
                                    <CopyIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip
                                  title="Eliminar cuenta"
                                  {...CONNECTED_ACCOUNTS_TOOLTIP_PROPS}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleDeleteClick(
                                        current ? "invitation" : "account",
                                        current
                                          ? current._id
                                          : linkedAccount._id,
                                        p,
                                      )
                                    }
                                    sx={CONNECTED_ACCOUNTS_ICON_BUTTON_ERROR_SX}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          </Stack>
                        ) : (
                          // Single-row layout for no connection
                          <Box className="flex items-center justify-between gap-3 w-full">
                            <Box className="flex items-center gap-2">
                              {platformIcon(p)}
                              <Typography variant="h5">
                                {platformDisplayName(p)}
                              </Typography>
                            </Box>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() =>
                                create.mutate({ days: 7, platform: p })
                              }
                              className="bg-[#1A1930] normal-case hover:brightness-110"
                            >
                              {`Conectar cuenta`}
                            </Button>
                          </Box>
                        )}
                      </Box>
                      {index !== 3 && <Divider />}
                    </>
                  );
                })}
              </Stack>
            </CardContent>
          </Card>
        </Box>

        {/* Confirmation Dialog for Delete */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={handleCancelDelete}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle
            sx={{ fontSize: "1.2rem", fontWeight: "bold", color: "text.primary" }}
          >
            Confirmar eliminación
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.primary">
              {itemToDelete?.type === "invitation"
                ? "¿Estás seguro de que quieres eliminar esta invitación?"
                : `¿Estás seguro de que quieres eliminar la cuenta de ${itemToDelete?.platform === "meta" ? "Instagram" : itemToDelete?.platform === "youtube" ? "YouTube" : itemToDelete?.platform === "tiktok" ? "TikTok" : "Spotify"}?`}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Esta acción no se puede deshacer.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete} color="primary">
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmDelete}
              color="error"
              variant="contained"
            >
              Eliminar
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={helpOpen}
          onClose={() => setHelpOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle
            sx={{ fontSize: "1.2rem", fontWeight: "bold", color: "text.primary" }}
          >
            ¿Cómo funcionan las invitaciones?
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.primary">
              Usa las invitaciones para conectar cuentas de Meta (Instagram),
              YouTube, TikTok o Spotify a este podcast.
            </Typography>
            <Box
              component="ul"
              sx={{
                listStyle: "disc",
                pl: 2.5,
                mt: 2,
                mb: 0,
                color: "text.primary",
                typography: "body2",
                "& li": { mb: 0.5 },
              }}
            >
              <li>
                Comparte el enlace de invitación con la persona que administra
                la cuenta de la plataforma.
              </li>
              <li>
                Al abrir el enlace, verá los detalles del podcast y autorizará
                acceso de solo lectura.
              </li>
              <li>
                Al completar el proceso, la cuenta quedará vinculada a este
                podcast.
              </li>
            </Box>
            <Typography variant="body2" color="text.primary" sx={{ pt: 2 }}>
              Nota: Solo puede existir una invitación activa por plataforma y
              por podcast.
            </Typography>
            <Typography variant="body2" color="text.primary" sx={{ pt: 2 }}>
              Para más información sobre cómo conectar cuentas correctamente,
              consulta nuestro{" "}
              <a
                href="https://polished-minute-aa1.notion.site/Dec-logo-conexi-n-de-cuentas-2628f617296b803f907ad12b3aa4e023"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#1976d2", textDecoration: "underline" }}
              >
                Decálogo de conexión de cuentas
              </a>
              .
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHelpOpen(false)}>Cerrar</Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          message={snackbar.message}
        />
      </Stack>
    </Stack>
  );
}

export default PodcastInvitations;
